/** Human-readable CRM labels (never show implementation keys in the UI). */

import {
  crmLeadSelectOptionLabel,
  standardIndustrySegmentSelectOptions,
  standardIndustrySelectOptions,
} from "@/lib/crmLeadModuleFields";

export const CRM_LEAD_SOURCES = ["Manual", "Web", "Import"] as const;
export type CrmLeadSource = (typeof CRM_LEAD_SOURCES)[number];

const SOURCE_ALIASES: Record<string, CrmLeadSource> = {
  "manual-entry": "Manual",
  manual: "Manual",
  "dashboard-form": "Manual",
  web: "Web",
  website: "Web",
  import: "Import",
  csv: "Import",
};

export function formatCrmSourceLabel(value?: string | null): string {
  const raw = (value || "").trim();
  if (!raw) return "";
  const mapped = SOURCE_ALIASES[raw.toLowerCase()];
  if (mapped) return mapped;
  if (raw.includes("_") || raw.includes("-")) {
    return raw
      .split(/[_-]+/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");
  }
  return raw;
}

export function formatCrmIndustryLabel(value?: string | null): string {
  const raw = (value || "").trim();
  if (!raw) return "";
  return crmLeadSelectOptionLabel(standardIndustrySelectOptions(), raw) || raw;
}

export function formatCrmIndustrySegmentLabel(value?: string | null): string {
  const raw = (value || "").trim();
  if (!raw) return "";
  return crmLeadSelectOptionLabel(standardIndustrySegmentSelectOptions(), raw) || raw;
}

export function normalizeCrmSourceForSave(value?: string | null): string {
  const labeled = formatCrmSourceLabel(value);
  return labeled || "Manual";
}

export const CRM_ACCOUNT_STATUSES = [
  "prospect",
  "active",
  "paused",
  "disqualified",
  "converted",
] as const;

/** CRM Account (post-conversion), not a pipeline Lead Account. */
export function crmAccountIsConverted(status?: string | null): boolean {
  return (status || "").trim().toLowerCase() === "converted";
}

/** Pipeline Lead Account eligible for lead attach (not a converted CRM Account). */
export function crmAccountIsLeadAccount(status?: string | null): boolean {
  return Boolean((status || "").trim()) && !crmAccountIsConverted(status);
}

export function formatCrmStatusLabel(value?: string | null): string {
  const raw = (value || "").trim();
  if (!raw) return "";
  return raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Leads list/detail Type column: short Account / Contact labels only. */
export function formatCrmLeadTypeLabel(value?: string | null): string {
  const raw = (value || "").trim().toLowerCase();
  if (raw === "company_only") return "Account";
  if (raw === "contact_only") return "Contact";
  if (raw === "company_plus_contact") return "Account/Contact";
  if (!raw) return "Account/Contact";
  return formatCrmStatusLabel(value);
}

export function formatOwnerStatusLabel(value?: string | null): string {
  const raw = (value || "").trim().toLowerCase();
  if (!raw) return "Unassigned";
  if (raw === "unassigned") return "Unassigned";
  if (raw === "assigned") return "Assigned";
  return formatCrmStatusLabel(value);
}

/** Missing owner is Unassigned, never "Blocked Unassigned Owner". */
export function formatOutreachStateLabel(
  state?: string | null,
  ownerId?: string | null,
): string {
  const hasOwner = Boolean(ownerId && String(ownerId).trim());
  const normalized = (state || "").trim().toLowerCase();
  if (!hasOwner || normalized === "unassigned" || normalized === "blocked_unassigned_owner") {
    return "Unassigned";
  }
  if (normalized === "ready") return "Ready";
  if (normalized === "active") return "Active";
  if (normalized === "paused") return "Paused";
  if (normalized === "blocked") return "Blocked";
  if (!normalized) return "Ready";
  return formatCrmStatusLabel(state);
}

type CrmAccountSearchFields = {
  id?: string | null;
  name?: string | null;
  domain?: string | null;
  email?: string | null;
  companyPhone?: string | null;
  industry?: string | null;
  city?: string | null;
  state?: string | null;
  status?: string | null;
  source?: string | null;
  outreachState?: string | null;
  ownerId?: string | null;
  ownerStatus?: string | null;
};

/** Rough stem so Supply matches Supplies / companies → company. */
function crmSearchStem(token: string): string {
  let s = token.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!s) return "";
  if (s.endsWith("ies") && s.length > 4) s = `${s.slice(0, -3)}y`;
  else if (
    (s.endsWith("ses") ||
      s.endsWith("xes") ||
      s.endsWith("zes") ||
      s.endsWith("ches") ||
      s.endsWith("shes")) &&
    s.length > 4
  ) {
    s = s.slice(0, -2);
  } else if (s.endsWith("es") && s.length > 4) s = s.slice(0, -2);
  else if (s.endsWith("s") && !s.endsWith("ss") && s.length > 3) s = s.slice(0, -1);
  // supply / supplies → shared "suppl"
  if (/[aeiouy]$/.test(s) && s.length > 4) s = s.slice(0, -1);
  return s;
}

function tokenizeSearchText(value: string): string[] {
  return value
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

/** Substring + token/stem match (Supply ↔ Supplies). */
export function crmTextMatchesQuery(
  haystack: string | null | undefined,
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const text = String(haystack || "").toLowerCase();
  if (!text) return false;
  if (text.includes(q)) return true;

  const queryTokens = tokenizeSearchText(q);
  if (queryTokens.length === 0) return false;
  const hayTokens = tokenizeSearchText(text);
  if (hayTokens.length === 0) return false;

  return queryTokens.every((qt) => {
    const qStem = crmSearchStem(qt);
    return hayTokens.some((ht) => {
      if (ht.includes(qt) || qt.includes(ht)) return true;
      const hStem = crmSearchStem(ht);
      if (!qStem || !hStem) return false;
      return (
        hStem === qStem ||
        hStem.startsWith(qStem) ||
        qStem.startsWith(hStem) ||
        (qStem.length >= 4 && hStem.length >= 4 && (hStem.includes(qStem) || qStem.includes(hStem)))
      );
    });
  });
}

function crmAccountSearchPairs(
  account: CrmAccountSearchFields,
): Array<{ label: string; value: string | null | undefined }> {
  return [
    { label: "Name", value: account.name },
    { label: "Domain", value: account.domain },
    { label: "Email", value: account.email },
    { label: "Phone", value: account.companyPhone },
    { label: "Industry", value: formatCrmIndustryLabel(account.industry) || account.industry },
    { label: "City", value: account.city },
    { label: "State", value: account.state },
    { label: "Status", value: formatCrmStatusLabel(account.status) },
    { label: "Source", value: formatCrmSourceLabel(account.source) },
    { label: "Outreach", value: formatOutreachStateLabel(account.outreachState, account.ownerId) },
    { label: "Owner", value: formatOwnerStatusLabel(account.ownerStatus) },
    { label: "Owner id", value: account.ownerId },
    { label: "Id", value: account.id },
  ];
}

export function crmAccountMatchesQuery(
  account: CrmAccountSearchFields,
  query: string,
): boolean {
  const q = query.trim();
  if (!q) return true;
  return crmAccountSearchPairs(account).some(({ value }) => crmTextMatchesQuery(value, q));
}

/** First visible reason a search matched, for list rows. */
export function crmAccountMatchReason(
  account: CrmAccountSearchFields,
  query: string,
): string | null {
  const q = query.trim();
  if (!q) return null;
  for (const { label, value } of crmAccountSearchPairs(account)) {
    if (!crmTextMatchesQuery(value, q)) continue;
    const shown = String(value || "").trim();
    return shown ? `${label}: ${shown}` : label;
  }
  return null;
}
